import axios from 'axios';
import securityUtils from '../utils/securityUtils';

const API_URL = '/auth/';

class AuthService {
  login(username, password) {
    // Encode credentials before sending (additional obfuscation layer)
    // Note: Primary security comes from HTTPS encryption
    const encoded = securityUtils.encodeCredentials(username, password);

    return axios
      .post(API_URL + 'signin', {
        username: encoded.u,
        password: encoded.p,
        encoded: true // Flag to tell backend these are encoded
      }, {
        withCredentials: true // Important: Send cookies with request
      })
      .then(response => {
        // Store only user info and access token in memory (not localStorage for production)
        // Refresh token is stored in httpOnly cookie by backend
        if (response.data.accessToken) {
          // Store minimal user data (no refresh token in localStorage)
          const userData = {
            id: response.data.id,
            username: response.data.username,
            email: response.data.email,
            roles: response.data.roles,
            accessToken: response.data.accessToken,
            expiresIn: response.data.expiresIn
          };

          // For production: Consider storing in memory only
          // For now, we'll use sessionStorage (cleared when browser closes)
          sessionStorage.setItem('user', JSON.stringify(userData));
        }

        // Clear sensitive data from memory
        securityUtils.clearSensitiveData(encoded);

        return response;
      })
      .catch(error => {
        // Clear sensitive data even on error
        securityUtils.clearSensitiveData(encoded);
        throw error;
      });
  }

  async refreshToken() {
    try {
      const response = await axios.post(API_URL + 'refresh', {}, {
        withCredentials: true // Send httpOnly cookie
      });

      if (response.data.accessToken) {
        const userData = {
          id: response.data.id,
          username: response.data.username,
          email: response.data.email,
          roles: response.data.roles,
          accessToken: response.data.accessToken,
          expiresIn: response.data.expiresIn
        };
        sessionStorage.setItem('user', JSON.stringify(userData));
      }

      return response;
    } catch (error) {
      // If refresh fails, user needs to login again
      this.logout();
      throw error;
    }
  }

  logout() {
    sessionStorage.removeItem('user');
    return axios.post(API_URL + 'signout', {}, {
      withCredentials: true // Send httpOnly cookie to revoke it
    }).then(response => {
      return response.data;
    }).catch(() => {
      // Even if server request fails, clear local data
      return { message: 'Logged out locally' };
    });
  }

  register(userData) {
    // Encode password before sending
    if (userData.password) {
      const encoded = securityUtils.encodeCredentials(userData.username, userData.password);
      const encodedData = {
        ...userData,
        username: encoded.u,
        password: encoded.p,
        encoded: true
      };

      return axios.post(API_URL + 'signup', encodedData)
        .finally(() => {
          securityUtils.clearSensitiveData(encoded);
          securityUtils.clearSensitiveData(encodedData);
        });
    }

    return axios.post(API_URL + 'signup', userData);
  }

  getCurrentUser() {
    // Try sessionStorage first (production), fallback to localStorage (backward compatibility)
    const user = sessionStorage.getItem('user') || localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  }

  // Check if token is expired or about to expire
  isTokenExpired() {
    const user = this.getCurrentUser();
    if (!user || !user.expiresIn) return true;

    // Check if token expires in less than 5 minutes
    const expirationTime = user.expiresIn;
    const currentTime = Date.now();
    const timeUntilExpiry = expirationTime - currentTime;

    return timeUntilExpiry < 5 * 60 * 1000; // 5 minutes
  }
}

export default new AuthService();